<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import { unparam, handleNumberInput, MAX_AMOUNT } from '@/utils';
import AppButton from '@/common/AppButton.vue';
import FormDesignation from '@/components/FormDesignation.vue';
import MatchingCard from '@/components/MatchingCard.vue';
import AmountMultiplier from '@/components/AmountMultiplier.vue';

export default {
  name: 'FormMainDetails',
  components: {
    AppButton,
    FormDesignation,
    MatchingCard,
    AmountMultiplier
  },
  props: {
    active: Boolean,
    showError: Boolean,
    fieldsDirty: Boolean,
    staticForm: {
      type: Boolean,
      default: false
    },
    formCampaignSettings: {
      type: Object,
      default: () => null
    },
    localAmount: {
      type: Number,
      default: null
    },
    localFrequency: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      isCustomInputOpen: false,
      customAmountChosen: false,
      customMultiplierAmount: null,
      multiplier: 1,
      amountValidationMessage: '',
      handleNumberInput,
      MAX_AMOUNT
    };
  },

  computed: {
    ...mapState('publicOrgSettings', ['settings']),
    ...mapGetters('publicOrgSettings', ['minimumDonationAmount']),
    ...mapGetters('campaign', ['matchingSettings']),

    ...mapState('campaign', {
      campaign: (campaign) => campaign,
      campaignSettings: (campaign) => campaign.settings
    }),
    settingsComputed() {
      if (this.formCampaignSettings) {
        return this.formCampaignSettings;
      }

      return this.staticForm ? this.settings : this.campaignSettings;
    },
    matchingSettingsComputed() {
      if (this.staticForm || this.formCampaignSettings) {
        return this.settingsComputed;
      }

      return this.matchingSettings;
    },
    effectiveMinimumAmount() {
      // Campaign checkout settings minimum overrides org minimum
      const campaignMinimum = this.settingsComputed?.minimumDonationAmount;

      if (campaignMinimum != null) {
        return Number(campaignMinimum);
      }
      // Fall back to org minimum from publicOrgSettings
      return this.minimumDonationAmount;
    },
    ...mapState('donation', {
      donation: (state) => state
    }),
    ...mapState({
      shouldOpenCustomAmount: (state) => state.isCustomAmountOpen
    }),
    ...mapGetters('donation', ['currencySymbol']),
    amount: {
      get() {
        return this.staticForm ? this.localAmount : this.donation.amount;
      },
      set(value) {
        if (this.staticForm) {
          this.$emit('update:local-amount', value);
        } else {
          this.SET_AMOUNT(value);
        }
      }
    },
    currentFrequency() {
      return this.staticForm ? this.localFrequency : this.donation.frequency;
    },
    coveringFees: {
      get() {
        return this.donation.payCoverFees;
      },
      set(value) {
        this.SET_PAY_COVER_FEES(value);
      }
    },

    multiplierSettings() {
      const settings = this.settingsComputed?.amountMultiplier;
      if (settings?.enabled !== true || typeof settings.label !== 'string' || !settings.label.trim() || !['counter', 'dropdown'].includes(settings.type)) {
        return null;
      }
      if (
        settings.type === 'dropdown' &&
        (!Array.isArray(settings.presets) || !settings.presets.length || !settings.presets.every((value) => Number.isSafeInteger(value) && value >= 1))
      ) {
        return null;
      }
      return settings;
    },
    suggestedAmounts() {
      if (!this.multiplierSettings) return this.baseSuggestedAmounts;
      return this.baseSuggestedAmounts.map((item) => ({
        ...item,
        amount: Math.round(item.amount * (this.multiplier ?? 1) * 100) / 100
      }));
    },
    baseSuggestedAmounts() {
      /* To avoid logic in template code:
       Unify suggested amounts (manually selected with
       description & smart suggestions without
    */
      // First render
      if (!this.settingsComputed?.suggestedAmounts) {
        return [];
      }
      const amounts = this.settingsComputed.suggestedAmounts;
      const suggestedAmounts = amounts[this.currentFrequency] || amounts.single;
      const isSmartSuggestions = this.settingsComputed.smartSuggestionsEnabled;

      const { conversionRates } = this.settingsComputed;
      const baseCurrency = this.settingsComputed?.currency || this.campaign.currency || this.settingsComputed?.campaignCurrency;
      if (this.settingsComputed?.suggestedAmountsAutoConversion && conversionRates && baseCurrency && this.donation.currency !== baseCurrency) {
        const targetCurrency = this.donation.currency;

        const exchangeRateObj =
          Array.isArray(conversionRates) && conversionRates.find((rate) => rate.fromCurrency === baseCurrency && rate.toCurrency === targetCurrency);

        if (exchangeRateObj && exchangeRateObj.rate) {
          const conversionRate = Number(exchangeRateObj.rate);

          if (isSmartSuggestions) {
            return suggestedAmounts.map((amount) => ({
              amount: this.convertAmount(amount, conversionRate),
              description: ''
            }));
          }

          return suggestedAmounts.map((item) => ({
            amount: this.convertAmount(item.amount, conversionRate),
            description: item.description
          }));
        }
      }

      if (isSmartSuggestions) {
        return suggestedAmounts.map((amount) => ({
          amount,
          description: ''
        }));
      }

      return suggestedAmounts;
    },

    someAmountHasDescription() {
      return this.suggestedAmounts.some((amount) => amount.description.length > 0);
    },

    expandColumns() {
      const longestDescriptionLength = this.suggestedAmounts.reduce((acc, suggestedAmount) => {
        return suggestedAmount.description.length > acc ? suggestedAmount.description.length : acc;
      }, 0);

      if (longestDescriptionLength > 45) {
        return 'expand-1';
      }

      if (longestDescriptionLength > 32) {
        return 'expand-2';
      }
      return null;
    },
    mexicanCampaignUsd() {
      const CAMPAIGNS = ['b3847820-8eea-11f0-816e-b38a43ed724a', '294bc4a0-cb7c-11f0-ac96-33e6212e15e0'];
      return CAMPAIGNS.includes(this.donation.campaignId) && this.donation.currency === 'usd';
    },
    amountErrorMessage() {
      if (this.amountValidationMessage && this.showError) {
        return this.amountValidationMessage;
      }

      if (!this.showError || this.amount) {
        return '';
      }

      return this.isCustomInputOpen ? 'Please enter an amount' : this.$t('donationAmount.errorMessage');
    }
  },

  watch: {
    multiplierSettings: {
      immediate: true,
      handler(settings) {
        this.changeMultiplier(settings?.type === 'dropdown' ? null : 1);
      }
    },
    amount(value) {
      if (!this.staticForm) {
        this.SET_AMOUNT(value);
      }
      if (this.validateAmount()) {
        this.amountValidationMessage = ''; // reset validation message
      }
    },
    active(newValue) {
      if (!newValue) {
        this.isCustomInputOpen = false;
      }
    },
    suggestedAmounts(newValue) {
      this.synchronizeDescription(newValue);
    },

    isCustomInputOpen(newValue) {
      this.$emit('is-custom-amount-open', newValue);
    },

    shouldOpenCustomAmount(newValue) {
      if (newValue) {
        this.openCustomAmountInput();
      }
    }
  },

  methods: {
    changeMultiplier(value) {
      if (value === null ? this.multiplierSettings?.type !== 'dropdown' : !Number.isSafeInteger(value) || value < 1) return;
      if (value === this.multiplier) return;
      const previousMultiplier = this.multiplier ?? 1;
      const index =
        this.customAmountChosen || this.isCustomInputOpen
          ? -1
          : this.suggestedAmounts.findIndex((item) => this.isSuggestedAmount(item.amount, item.description));
      this.multiplier = value;
      if (index !== -1) {
        const item = this.suggestedAmounts[index];
        this.selectAmount(item.amount, item.description);
      } else if (this.multiplierSettings && Number.isFinite(this.amount) && this.amount > 0) {
        const base = this.customMultiplierAmount?.total === this.amount ? this.customMultiplierAmount.base : this.amount / previousMultiplier;
        const total = Math.round(base * (value ?? 1) * 100) / 100;
        this.customMultiplierAmount = { base, total };
        this.customAmountChosen = true;
        this.amount = total;
      }
    },
    // **** Business logic
    ...mapMutations(['SET_CUSTOM_AMOUNT_OPEN']),
    ...mapMutations('donation', ['SET_AMOUNT', 'SET_AMOUNT_DESCRIPTION', 'SET_PAY_COVER_FEES', 'SET_FREQUENCY']),

    convertAmount(amount, conversionRate) {
      const convertedAmount = amount * conversionRate;
      return Math.round(convertedAmount);
    },

    selectAmount(amount, description) {
      this.customAmountChosen = false;
      this.customMultiplierAmount = null;
      this.amount = amount;
      if (!this.staticForm) {
        this.SET_AMOUNT_DESCRIPTION(description);
      }
    },

    validateAmount() {
      if (this.amount == null) {
        return false;
      }

      // suggested amounts
      if (!this.multiplierSettings && !this.isCustomAmountSelected(this.amount)) {
        return true;
      }
      const minAmount = this.effectiveMinimumAmount;
      if (this.amount < minAmount) {
        this.amountValidationMessage = `Minimum donation amount is ${this.currencySymbol}${minAmount}`;
        return false;
      }
      if (this.amount > MAX_AMOUNT) {
        this.amountValidationMessage = `Maximum donation amount is ${this.currencySymbol}${MAX_AMOUNT}`;
        return false;
      }

      return true;
    },

    // **** UI
    selectCustomAmount() {
      const isAmountValid = this.validateAmount();
      if (!isAmountValid) {
        return;
      }
      this.isCustomInputOpen = false;
    },

    async openCustomAmountInput() {
      this.customAmountChosen = !!this.multiplierSettings;
      if (this.isCustomInputOpen) {
        return;
      }

      this.isCustomInputOpen = true;
      if (!this.staticForm) {
        this.SET_AMOUNT_DESCRIPTION(null);
      }
      await this.$nextTick();
      const numericInputComponent = this.$refs.customAmountInput;
      if (numericInputComponent && numericInputComponent.inputRef) {
        numericInputComponent.inputRef.focus();
        numericInputComponent.inputRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },

    handleCustomAmountInputChange() {
      this.amountValidationMessage = '';
    },

    handleCustomAmountValidationError({ message }) {
      this.amountValidationMessage = message;
    },

    isCustomAmountSelected(amount) {
      return amount && (this.customAmountChosen || !this.isSuggestedAmount(amount));
    },

    isSuggestedAmount(amount, description = null) {
      if (this.customAmountChosen) return false;
      const suggestedAmounts = this.suggestedAmounts.map((suggestedAmount) => suggestedAmount.amount);
      if (!suggestedAmounts.includes(amount)) {
        return false;
      }

      const amountDescription = this.donation.amountDescription;
      const isUniqueAmount = this.suggestedAmounts.filter((suggestedAmount) => suggestedAmount.amount === amount).length === 1;
      if (description && !isUniqueAmount) {
        return amount === this.amount && amountDescription === description;
      }

      return amount === this.amount;
    },

    async synchronizeDescription(suggestedAmounts) {
      if (this.staticForm) return;
      // This is needed to automatically select the description when the amount is selected via the JS snippet
      // This can only work when there's only one suggested amount with the given amount, if this is not the case
      // the description will not be selected. It will have to be passed in to the JS snippet using the `description` param
      if (suggestedAmounts.filter((suggestedAmount) => suggestedAmount.amount === this.amount).length > 1) {
        return console.warn('Multiple suggested amounts with the same amount, description will not be selected');
      }
      const description = this.suggestedAmounts.find((suggestedAmount) => suggestedAmount.amount === this.amount)?.description || null;
      this.SET_AMOUNT_DESCRIPTION(description);
    },

    // **** Helper methods

    handleFrequencyParams() {
      if (this.staticForm) return;
      const frequencies = {
        'one-time': 'single',
        weekly: 'weekly',
        monthly: 'monthly',
        annually: 'yearly'
      };

      const queryParams = unparam(location.search);
      const frequencyParam = queryParams.frequency;

      if (!frequencyParam) {
        return;
      }

      const frequency = frequencies[frequencyParam.toLowerCase()];
      this.SET_FREQUENCY(frequency);
    }
  },

  created() {
    this.handleFrequencyParams();
  },
  mounted() {
    if (this.settingsComputed.hideOtherButton && this.suggestedAmounts.length === 1) {
      this.selectAmount(this.suggestedAmounts[0].amount, this.suggestedAmounts[0].description);
    }

    // this handles the case where the amount is set from the JS via the `description` param
    if (!this.amount && this.donation.amountDescription) {
      const amount = this.suggestedAmounts.find((suggestedAmount) => suggestedAmount.description === this.donation.amountDescription);
      if (amount) {
        this.selectAmount(amount.amount, amount.description);
      }
    }

    // TEMP: https://hameirlaaretz.com/?campaign=mexico
    const ENABLED_ES_CAMPAIGNS = ['b3847820-8eea-11f0-816e-b38a43ed724a', '294bc4a0-cb7c-11f0-ac96-33e6212e15e0'];
    if (ENABLED_ES_CAMPAIGNS.includes(this.settingsComputed.campaignId)) {
      this.$i18n.locale = 'es';
    }
  }
};
</script>

<template>
  <section ref="form-main-details" :class="{ 'section-has-error': showError && !amount }" class="form-main-details mb-8">
    <matching-card :settings="matchingSettingsComputed" class="matching-card-section" />
    <amount-multiplier v-if="multiplierSettings" :settings="multiplierSettings" :value="multiplier" @input="changeMultiplier" />
    <div ref="suggestedAmounts" class="suggested-amounts" :class="expandColumns">
      <app-button
        v-for="(suggestedAmount, i) in suggestedAmounts"
        :key="i"
        variant="outline"
        :selected="!isCustomInputOpen && isSuggestedAmount(suggestedAmount.amount, suggestedAmount.description)"
        @click="
          selectAmount(suggestedAmount.amount, suggestedAmount.description);
          isCustomInputOpen = false;
        "
      >
        <div class="flex items-center justify-center">
          <h3 class="main flex items-center">
            <template v-if="settingsComputed.buttonTextPriority === 'description' && suggestedAmount.description.length > 0">
              {{ suggestedAmount.description }}
            </template>
            <template v-else>
              <span v-if="mexicanCampaignUsd" class="font-nunito-sans font-normal text-xs text-black-01 text-center mr-1">USD</span>
              <span v-else class="currency-symbol">{{ currencySymbol }}</span>
              <span>{{ suggestedAmount.amount | number }}</span>
            </template>
          </h3>
          <frequency-hint v-if="settingsComputed.buttonTextPriority !== 'description' || !suggestedAmount.description.length" :frequency="currentFrequency" />
        </div>
        <p
          v-if="suggestedAmount.description.length > 0"
          class="subtext-small-text-semibold text-black-01"
          :class="{ 'flex items-center justify-center': settingsComputed.buttonTextPriority === 'description' }"
        >
          <template v-if="settingsComputed.buttonTextPriority === 'description'">
            <span class="currency-symbol">{{ currencySymbol }}</span>
            <span>{{ suggestedAmount.amount | number }}</span>
            <frequency-hint :frequency="currentFrequency" />
          </template>
          <template v-else>
            {{ suggestedAmount.description }}
          </template>
        </p>
      </app-button>
      <app-button
        v-if="!settingsComputed.hideOtherButton || isCustomAmountSelected(amount)"
        class="custom-amount"
        variant="outline"
        @click="openCustomAmountInput"
        :selected="isCustomInputOpen || isCustomAmountSelected(amount)"
        :focused="isCustomInputOpen"
      >
        <div class="flex items-center justify-center">
          <h3 class="amount">
            <span class="label" v-if="!isCustomAmountSelected(amount)" @click="openCustomAmountInput">{{ $t('donationAmount.other') }}</span>
            <span v-if="isCustomAmountSelected(amount)" class="selected-label">
              <span v-if="mexicanCampaignUsd" class="font-nunito-sans font-normal text-xs text-black-01 text-center mr-1">USD</span>
              <span v-else class="currency-symbol">{{ currencySymbol }}</span>
              <span>{{ amount || 0 | number }}</span>
            </span>
          </h3>
          <frequency-hint v-if="isCustomAmountSelected(amount)" :frequency="currentFrequency" />
        </div>
        <p v-if="isCustomAmountSelected(amount) && someAmountHasDescription" class="subtext-small-text-semibold text-black-01">Other amount</p>
      </app-button>
    </div>
    <transition name="expandx3">
      <div v-if="isCustomInputOpen" class="relative mt-2.5">
        <numeric-input
          v-model="amount"
          :max="MAX_AMOUNT"
          :min="effectiveMinimumAmount"
          :currency-symbol="currencySymbol"
          :show-error-message="false"
          ref="customAmountInput"
          @input-change="handleCustomAmountInputChange"
          @validation-error="handleCustomAmountValidationError"
          @focusout="selectCustomAmount"
          @keyup.enter="selectCustomAmount"
        >
          <template #currency-symbol>
            <span v-if="mexicanCampaignUsd" class="font-nunito-sans font-normal text-xs text-black-01 text-center mr-1">USD</span>
            <span v-else class="currency-symbol">{{ currencySymbol }}</span>
          </template>
          <template #suffix>
            <frequency-hint :frequency="currentFrequency" />
          </template>
        </numeric-input>
      </div>
    </transition>
    <div v-if="amountErrorMessage" class="amount-error mt-1">
      <span class="custom-error-message">{{ amountErrorMessage }}</span>
    </div>
    <template v-if="!staticForm">
      <form-designation class="mt-4" />
    </template>
  </section>
</template>
